import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexGrid,
  ApexLegend,
  ApexPlotOptions,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  NgApexchartsModule,
} from 'ng-apexcharts';

@Component({
  selector: 'finance-pwa-repayment-chart',
  standalone: true,
  imports: [NgApexchartsModule],
  template: `
    <apx-chart
      [series]="series"
      [chart]="chart"
      [plotOptions]="plotOptions"
      [xaxis]="xaxis"
      [yaxis]="yaxis"
      [grid]="grid"
      [tooltip]="tooltip"
      [legend]="legend"
      [dataLabels]="dataLabels"
    ></apx-chart>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RepaymentChartComponent {
  @Input() set categories(values: string[] | null) {
    this.xaxis = {
      ...this.xaxis,
      categories: values?.length ? values : this.defaultCategories,
    };
  }

  @Input() set projected(values: number[] | null) {
    this.series = [
      { name: 'Total Target', data: values?.length ? values : [0] },
      { name: 'Contributed', data: this.actualValues },
    ];
  }

  @Input() set actual(values: number[] | null) {
    this.actualValues = values?.length ? values : [0];
    this.series = [
      { name: 'Total Target', data: this.series[0].data as number[] },
      { name: 'Contributed', data: this.actualValues },
    ];
  }

  private actualValues: number[] = [0];
  private readonly defaultCategories = ['Asset 1', 'Asset 2', 'Asset 3', 'Asset 4'];

  series: ApexAxisChartSeries = [
    { name: 'Total Target', data: [0] },
    { name: 'Contributed', data: [0] },
  ];

  chart: ApexChart = {
    type: 'bar',
    height: 250,
    toolbar: { show: false },
  };

  plotOptions: ApexPlotOptions = {
    bar: {
      borderRadius: 6,
      columnWidth: '54%',
    },
  };

  xaxis: ApexXAxis = {
    categories: this.defaultCategories,
    labels: {
      style: {
        colors: '#40484c',
        fontFamily: 'Inter',
      },
    },
  };

  yaxis: ApexYAxis = {
    labels: {
      formatter: (value) => this.formatCompactPounds(value),
      style: {
        colors: '#40484c',
        fontFamily: 'Inter',
      },
    },
  };

  grid: ApexGrid = {
    borderColor: '#dde3e8',
  };

  legend: ApexLegend = {
    position: 'top',
    horizontalAlign: 'left',
  };

  tooltip: ApexTooltip = {
    y: {
      formatter: (value) => this.formatCompactPounds(value),
    },
  };

  dataLabels: ApexDataLabels = {
    enabled: false,
  };

  private formatCompactPounds(value: number): string {
    const rounded = Math.round(value);
    if (Math.abs(rounded) < 1000) {
      return `£${rounded}`;
    }

    const compact = rounded / 1000;
    const formatted = Number.isInteger(compact)
      ? compact.toString()
      : compact.toFixed(1).replace(/\.0$/, '');

    return `£${formatted}k`;
  }
}
